import React, { useState, useMemo } from 'react';
import { Play, Edit2, Copy, Trash2, Users, Building2, Globe, Plus, Loader2, AlertCircle } from 'lucide-react';
import { useSavedSearches } from '../../hooks/useSavedSearches';
import { useAuth } from '../../hooks/useAuth';
import SearchBuilder from './SearchBuilder';

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `há ${m}min`;
  const h = Math.floor(m / 60); if (h < 48) return `há ${h}h`;
  const d = Math.floor(h / 24); return `há ${d}d`;
}

export default function AudienciasList() {
  const { user } = useAuth();
  const { searches, loading, error, duplicateSearch, deleteSearch, refetch } = useSavedSearches();
  const [tab, setTab] = useState('minhas');
  const [builderOpen, setBuilderOpen] = useState(null); // { mode, savedSearch? } | null

  const filtered = useMemo(() => {
    const email = user?.email;
    if (tab === 'minhas') return searches.filter((s) => !s.is_template && s.owner_email === email);
    if (tab === 'time') return searches.filter((s) => !s.is_template && s.is_shared && s.owner_email !== email);
    if (tab === 'templates') return searches.filter((s) => s.is_template).sort((a, b) => (a.search_target || '').localeCompare(b.search_target || '') || a.name.localeCompare(b.name));
    return [];
  }, [searches, tab, user]);

  const handleDuplicate = async (s) => {
    const res = await duplicateSearch(s, user?.email);
    if (res.success) { setTab('minhas'); refetch(); }
    else alert(`Erro: ${res.error}`);
  };

  const handleDelete = async (s) => {
    const res = await deleteSearch(s.id);
    if (res.success === false && res.error) alert(`Erro: ${res.error}`);
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex gap-1">
          {['minhas', 'time', 'templates'].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-sm rounded ${tab === t ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {t === 'minhas' && '⭐ Minhas'}
              {t === 'time' && '🌐 Do time'}
              {t === 'templates' && '📚 Templates'}
            </button>
          ))}
        </div>
        <button onClick={() => setBuilderOpen({ mode: 'create' })} className="ml-auto px-3 py-1.5 text-sm bg-violet-600 text-white rounded hover:bg-violet-700 flex items-center gap-1">
          <Plus size={14} /> Nova busca
        </button>
      </div>

      {loading && <div className="p-6 text-center text-sm text-slate-500"><Loader2 size={16} className="animate-spin inline mr-1" /> Carregando...</div>}
      {error && !loading && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14} /> {error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded">
          {tab === 'minhas' && 'Nenhuma audiência salva. Crie sua primeira busca ou duplique um template.'}
          {tab === 'time' && 'Nenhuma audiência compartilhada pelo time.'}
          {tab === 'templates' && 'Nenhum template disponível.'}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((s) => {
            const isTemplate = s.is_template;
            const canEdit = !isTemplate && s.owner_email === user?.email;
            return (
              <div key={s.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-800">{s.name}</h3>
                    {s.description && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{s.description}</p>}
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${s.search_target === 'people' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {s.search_target === 'people' ? <Users size={10} /> : <Building2 size={10} />}
                        {s.search_target}
                      </span>
                      {s.is_shared && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-800"><Globe size={10} /> shared</span>}
                      {isTemplate && <span className="px-1.5 py-0.5 rounded text-[10px] bg-violet-100 text-violet-800">📚 template</span>}
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-[11px] text-slate-500">
                  {s.last_run_at ? (
                    <span>Última: <strong>{timeAgo(s.last_run_at)}</strong> · {s.last_run_count ?? 0} resultados · {s.total_runs} runs totais</span>
                  ) : (
                    <span className="italic">Nunca executada</span>
                  )}
                </div>

                <div className="mt-3 flex gap-1 flex-wrap">
                  <button onClick={() => setBuilderOpen({ mode: 'execute', savedSearch: s })} className="px-2 py-1 text-[11px] bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1">
                    <Play size={11} /> Executar
                  </button>
                  {canEdit && (
                    <button onClick={() => setBuilderOpen({ mode: 'edit', savedSearch: s })} className="px-2 py-1 text-[11px] bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1">
                      <Edit2 size={11} /> Editar
                    </button>
                  )}
                  <button onClick={() => handleDuplicate(s)} className="px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded flex items-center gap-1">
                    <Copy size={11} /> Duplicar
                  </button>
                  {canEdit && (
                    <button onClick={() => handleDelete(s)} className="px-2 py-1 text-[11px] text-red-600 hover:bg-red-50 rounded flex items-center gap-1">
                      <Trash2 size={11} /> Arquivar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {builderOpen && (
        <SearchBuilder
          mode={builderOpen.mode}
          savedSearch={builderOpen.savedSearch}
          onClose={() => setBuilderOpen(null)}
          onSearchComplete={() => { setBuilderOpen(null); refetch(); }}
        />
      )}
    </>
  );
}
